import axios from "axios";
import config from "@config/config";

const STORAGE_KEY = "ww_admin_auth";
const MARKETPLACE_KEY = "ww_active_marketplace";

const api = axios.create({
  baseURL: config.apiBaseUrl,
  headers: { "Content-Type": "application/json" },
});

function getStoredToken() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.token || null;
  } catch {
    return null;
  }
}

function getActiveMarketplace() {
  try {
    return localStorage.getItem(MARKETPLACE_KEY) || null;
  } catch {
    return null;
  }
}

api.interceptors.request.use(
  (cfg) => {
    const token = getStoredToken();
    if (token) {
      cfg.headers = cfg.headers || {};
      cfg.headers.Authorization = `Bearer ${token}`;
    }

    // Tell the API which marketplace (franchise) we're viewing.
    // Honoured server-side only for super admins; ignored for everyone else.
    // Requests may opt out (cfg.skipMarketplace) when they carry their own
    // explicit franchise_id scope and must not be narrowed to one marketplace.
    const marketplace = getActiveMarketplace();
    if (marketplace && !cfg.skipMarketplace) {
      cfg.headers = cfg.headers || {};
      cfg.headers["X-Marketplace-Id"] = marketplace;
    }

    return cfg;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error || {};
    if (!response || !config) {
      return Promise.reject(error);
    }

    const status = response.status;
    const method = (config.method || "").toLowerCase();
    const authHeader = config.headers?.Authorization;

    const isPreflight = method === "options";
    const hadToken = !!authHeader;

    if (status === 401  && !isPreflight) {
      if (!config._retry) {
        config._retry = true;

        await new Promise((resolve) => setTimeout(resolve, 1000));

        try {
          return await api(config);
        } catch (retryError) {
          // retry also failed → logout
          localStorage.removeItem(STORAGE_KEY);
          if (window.location.pathname !== "/login") {
            const from = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.href = "/login?from=" + from;
          }
          return Promise.reject(retryError);
        }
      }

      // safety fallback (should not normally reach here)
      localStorage.removeItem(STORAGE_KEY);
      if (window.location.pathname !== "/login") {
        const from = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = "/login?from=" + from;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
